/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LineTab, LineTabs } from '../../components/react/line_tabs/line_tabs';
import NoData from '../../components/react/no_data/no_data';
import Tooltip from '../../components/react/tooltip/tooltip';
import Pagination from '../../components/react/pagination/pagination';
import { AlertHandler } from '../../components/react/alert_handler/alert_handler';
import { createQueryString } from '../../utilities/utilities';
import { getOrganizationContacts } from '../../services/organizations/organizations.service';
import { showErrors } from '../../utilities/fetch';
import ModifyOrganizationStaff from './components/modify_organization_staff/modify_organization_staff';
import OrganizationContact from './components/organization_contact/organization_contact';
import styles from './organization_contacts.module.scss';
/* eslint-enable max-len */

const RECRUITER_LINK_BASE = '/recruiter/search';

const alertHandler = new AlertHandler();

const CONTACTS_PER_PAGE = 9;

const OrganizationContacts = ({ organizationName, organizationId, isAdmin }) => {
  const [type, setType] = useState('official');
  const [modifyingContact, setModifyingContact] = useState(null);
  const [changingStaff, setChangingStaff] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  /**
   * Get contacts from backend
   *
   * @param {number} [page] - The page to fetch
   * @returns {undefined}
   */
  const fetchContacts = (page) => {
    if (fetching) return;
    setFetching(true);
    getOrganizationContacts(organizationId, type, CONTACTS_PER_PAGE, page || currentPage)
      .then((res) => {
        setContacts(res.data);
        setTotal(res.total);
      })
      .catch((error) => showErrors(error, alertHandler))
      .finally(() => setFetching(false));
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchContacts(1);
  }, [type]);

  useEffect(() => {
    fetchContacts();
  }, [organizationId, currentPage, CONTACTS_PER_PAGE]);

  const noContacts = (
    <NoData
      small
      className={`mt-48 ${fetching ? 'loading' : ''}`}
      textDescription={`No ${type} contacts`}
    />
  );

  /*
   * Opens view to modify contact
   * @param {Object} contact - the contact selected to be modified
   * @returns {undefined}
   */
  const onModifyContact = (contact) => {
    setModifyingContact(contact);
    setChangingStaff(true);
  };

  const showContacts = (
    <>
      <div className={`${fetching ? 'loading' : ''} ${styles.contactsList} mb-4`}>
        {contacts.map((contact) => (
          <OrganizationContact
            key={contact.id}
            {...contact}
            canEdit={isAdmin}
            organizationId={organizationId}
            modifyContact={() => onModifyContact(contact)}
            contactDeleted={() => fetchContacts()}
            type={type}
          />
        ))}
      </div>
      {total > CONTACTS_PER_PAGE && (
        <Pagination
          currentPage={currentPage}
          maxPages={Math.ceil(total / CONTACTS_PER_PAGE)}
          handlePageSelected={setCurrentPage}
          disabled={fetching}
        />
      )}
    </>
  );

  const contactsDisplay = contacts.length ? showContacts : noContacts;

  const addStaffOption = (
    <a
      className={`btn btn-sm btn-outline-info ${styles.addStaffBtn}`}
      onClick={() => setChangingStaff(true)}
      data-testid='add-staff'
    >
      <i className='fal icon-plus-circle d-none d-sm-inline me-15'></i>
      Add <span className='d-none d-sm-inline'>Staff</span>
    </a>
  );
  const tooltipMessage = `
    Contacts shown here have experience working for this
    organization and have recently updated their Devex profile.
  `;

  const onExitModifyingStaff = () => {
    setModifyingContact(null);
    setChangingStaff(false);
    fetchContacts();
  };

  const modifyStaff = (
    <ModifyOrganizationStaff
      {...modifyingContact}
      organizationId={organizationId}
      exit={onExitModifyingStaff}
      type={type}
    />
  );

  const header = (
    <div className='d-flex justify-content-center align-items-center mb-48'>
      <h2 className='section-title'>
        Contact {organizationName}
        <Tooltip message={tooltipMessage} placement='top'>
          <i className={`fal icon-info-circle text-blue ms-15 ${styles.contactsInfoTooltip}`}></i>
        </Tooltip>
      </h2>
    </div>
  );

  const recruiterLink = (
    <div className='text-center mt-48'>
      <a href={RECRUITER_LINK_BASE + createQueryString({ query: organizationName })}>
        Connect with professionals at {organizationName}
      </a>
    </div>
  );

  return (
    <div
      className={`${styles.contactsContainer} pb-4`}
      id='contact-section'
      data-testid='organization-contacts-component'
    >
      {header}
      <div className={styles.contactsBody}>
        {!changingStaff && isAdmin && addStaffOption}
        <LineTabs activeKey={type} onSelect={(newType) => setType(newType)}>
          <LineTab title='Official Staff' eventKey='official'></LineTab>
          <LineTab title='Current Staff' eventKey='current'></LineTab>
          <LineTab title='Former Staff' eventKey='former'></LineTab>
        </LineTabs>
        {changingStaff ? modifyStaff : contactsDisplay}
        {recruiterLink}
      </div>
    </div>
  );
};

OrganizationContacts.propTypes = {
  organizationName: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

export default OrganizationContacts;
